<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    
    <div class="p-6">
      <div class="bg-white rounded-lg shadow-lg overflow-hidden">

        <div class="flex flex-col lg:flex-row">
          <!-- Left Panel - Waypoints List -->
          <div class="lg:w-1/4 border-r border-gray-200 p-4 bg-gray-50 max-h-[700px] overflow-y-auto">
            <h2 class="text-lg font-semibold mb-4 flex items-center">
              <span class="mr-2">📍</span> Waypoints ({{ waypoints.length }})
            </h2>

            <!-- Address search -->
            <div class="mb-4">
              <input
                type="text"
                v-model="searchQuery"
                @input="onSearchInput"
                placeholder="Search address to add..."
                class="w-full p-2 border rounded"
                autocomplete="off"
              />
              <div v-if="showSuggestions && predictions.length" class="mt-1 bg-white border rounded shadow max-h-60 overflow-y-auto z-10">
                <div
                  v-for="pred in predictions"
                  :key="pred.place_id"
                  @click="selectPrediction(pred)"
                  class="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {{ pred.description }}
                </div>
              </div>
            </div>
            
            <draggable 
              v-model="waypoints" 
              item-key="id"
              @end="onDragEnd"
              class="space-y-2 mb-4"
              handle=".drag-handle"
            >
              <template #item="{element: waypoint, index}">
                <div 
                  class="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow border-l-4"
                  :class="{
                    'border-green-500': index === 0,
                    'border-red-500': index === waypoints.length - 1,
                    'border-blue-500': index > 0 && index < waypoints.length - 1,
                    'ring-2 ring-blue-400': selectedWaypointId === waypoint.id
                  }"
                >
                  <div class="flex items-start justify-between">
                    <div class="drag-handle cursor-move mr-3 text-gray-400 hover:text-gray-600 flex-shrink-0 pt-1">
                      ≡
                    </div>
                    <div class="flex-1" @click="selectWaypoint(waypoint)">
                      <div class="flex items-center mb-1">
                        <span class="font-bold text-sm mr-2">
                          {{ index === 0 ? '🚩 Start' : index === waypoints.length - 1 ? '🏁 End' : `#${index + 1}` }}
                        </span>
                      </div>
                      <div class="text-xs text-gray-600">
                        <div>Lat: {{ waypoint.lat.toFixed(6) }}</div>
                        <div>Lng: {{ waypoint.lng.toFixed(6) }}</div>
                      </div>
                    </div>
                    <button 
                      v-if="waypoints.length > 2"
                      @click.stop="removeWaypoint(waypoint.id)"
                      class="ml-2 text-red-500 hover:text-red-700 text-xl flex-shrink-0"
                      title="Remove waypoint"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

          <!-- Center - Map -->
          <div class="lg:w-2/4 relative">
            <div v-if="isCalculatingRoute" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div class="text-center">
                <Loader />
                <p class="mt-2 text-gray-600">Calculating route...</p>
              </div>
            </div>
            
            <GoogleMap
              ref="googleMap"
              :api-key="googleMapsApiKey"
              :libraries="['places']"
              style="width: 100%; height: 700px;"
              :center="mapCenter"
              :zoom="12"
              @click="handleMapClick"
            >
              <!-- Waypoint Markers -->
              <Marker
                v-for="(waypoint, index) in waypoints"
                :key="waypoint.id"
              :options="{
                position: { lat: waypoint.lat, lng: waypoint.lng },
                label: {
                  text: index === 0 ? 'A' : index === waypoints.length - 1 ? 'B' : String(index + 1),
                  color: 'white',
                  fontWeight: 'bold'
                },
                  icon: {
                    url: index === 0 
                      ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                      : index === waypoints.length - 1
                      ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                      : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                  },
                  title: `Waypoint ${index + 1}`
                }"
              />
            </GoogleMap>
          </div>

          <div class="lg:w-1/4 p-4 bg-gray-50">
            <h2 class="text-lg font-semibold mb-4 flex items-center">
              <span class="mr-2">⚙️</span> Controls
            </h2>

            <!-- Route Optimization Toggle -->
            <div class="mb-4 bg-white rounded-lg p-4 shadow-sm">
              <label class="flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  v-model="optimizeRoute"
                  @change="calculateRoute"
                  class="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                />
                <div>
                  <div class="font-semibold text-gray-800">Optimize Route</div>
                  <div class="text-xs text-gray-500">Auto-reorder for shortest path</div>
                </div>
              </label>
            </div>

            <!-- Recalculate Button -->
            <button
              @click="calculateRoute"
              :disabled="isCalculatingRoute || waypoints.length < 2"
              class="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold mb-4 transition-colors"
            >
              🔄 Recalculate Route
            </button>

            <!-- Route Information -->
            <div v-if="routeData" class="bg-white rounded-lg p-4 shadow-sm">
              <h3 class="font-semibold mb-3 text-gray-800">📊 Route Information</h3>
              
              <div class="space-y-3">
                <div class="flex items-center justify-between border-b pb-2">
                  <span class="text-gray-600 text-sm">Total Distance:</span>
                  <span class="font-bold text-blue-600">{{ routeData.distance }}</span>
                </div>
                
                <div class="flex items-center justify-between border-b pb-2">
                  <span class="text-gray-600 text-sm">Estimated Duration:</span>
                  <span class="font-bold text-blue-600">{{ routeData.duration }}</span>
                </div>
                
                <div class="flex items-center justify-between">
                  <span class="text-gray-600 text-sm">Waypoints:</span>
                  <span class="font-bold text-gray-800">{{ waypoints.length }}</span>
                </div>
              </div>
            </div>

            <!-- Error Display -->
            <div v-if="errorMessage" class="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 class="font-semibold text-red-800 mb-2">⚠️ Error</h3>
              <p class="text-sm text-red-600">{{ errorMessage }}</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GoogleMap, Marker } from 'vue3-google-map';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import draggable from 'vuedraggable';

export default {
  components: {
    GoogleMap,
    Marker,
    BreadCrumbs,
    Loader,
    draggable
  },
  
  props: {
    initialCoordinates: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Test Route', href: route('test.route') }
      ],
      waypoints: [],
      mapCenter: { lat: 45.0732, lng: 7.6827 },
      directionsService: null,
      directionsRenderer: null,
      directionsRenderers: [],
      isCalculatingRoute: false,
      routeData: null,
      errorMessage: '',
      optimizeRoute: true,
      selectedWaypointId: null,
      nextWaypointId: 21,
      searchQuery: '',
      predictions: [],
      showSuggestions: false,
      searchDebounce: null,
      placesService: null,
    };
  },

  mounted() {
    this.waypoints = [...this.initialCoordinates];
    
    this.$nextTick(() => {
      setTimeout(() => {
        this.initializeDirectionsService();
        this.initPlacesService();
        this.calculateRoute();
      }, 1000);
    });
  },

  methods: {
    onDragEnd() {
      this.optimizeRoute = false;
      this.calculateRoute();
    },

    clearAllRenderers() {
      if (this.directionsRenderer) {
        try { this.directionsRenderer.setDirections({ routes: [] }); } catch (e) {}
      }
      if (this.directionsRenderers && this.directionsRenderers.length) {
        this.directionsRenderers.forEach(r => {
          try { r.setMap(null); } catch (e) {}
        });
        this.directionsRenderers = [];
      }
    },

    requestDirections(request) {
      return new Promise((resolve) => {
        this.directionsService.route(request, (result, status) => {
          resolve({ result, status });
        });
      });
    },

    async calculateChunkedRoute() {
      if (!this.directionsService) {
        this.initializeDirectionsService();
        if (!this.directionsService) {
          this.errorMessage = 'Google Maps Directions Service not initialized';
          return;
        }
      }

      this.isCalculatingRoute = true;
      this.errorMessage = '';
      this.routeData = null;
      this.clearAllRenderers();

      let totalDistance = 0;
      let totalDuration = 0;

      const n = this.waypoints.length;
      let startIndex = 0;
      while (startIndex < n - 1) {
        const endIndex = Math.min(startIndex + 24, n - 1);

        const origin = this.waypoints[startIndex];
        const destination = this.waypoints[endIndex];
        const middle = this.waypoints.slice(startIndex + 1, endIndex);

        const waypointsForApi = middle.map(wp => ({ location: { lat: wp.lat, lng: wp.lng }, stopover: true }));
        const request = {
          origin: { lat: origin.lat, lng: origin.lng },
          destination: { lat: destination.lat, lng: destination.lng },
          waypoints: waypointsForApi,
          optimizeWaypoints: false,
          travelMode: google.maps.TravelMode.DRIVING
        };

        const { result, status } = await this.requestDirections(request);
        if (status === google.maps.DirectionsStatus.OK) {
          const renderer = new google.maps.DirectionsRenderer({
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: { strokeColor: '#2563eb', strokeWeight: 4, strokeOpacity: 0.8 }
          });
          renderer.setMap(this.$refs.googleMap.map);
          renderer.setDirections(result);
          this.directionsRenderers.push(renderer);

          const route = result.routes[0];
          route.legs.forEach(leg => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
          });
        } else {
          this.isCalculatingRoute = false;
          this.errorMessage = `Chunk routing failed (${startIndex + 1}-${endIndex + 1}): ${status}`;
          return;
        }

        startIndex = endIndex;
      }

      this.routeData = {
        distance: (totalDistance / 1000).toFixed(2) + ' km',
        duration: this.formatDuration(totalDuration)
      };

      this.isCalculatingRoute = false;
    },

    reorderWaypoints(optimizedOrder) {
      if (this.waypoints.length < 3) {
        return;
      }

      const start = this.waypoints[0];
      const end = this.waypoints[this.waypoints.length - 1];
      const middleWaypoints = this.waypoints.slice(1, -1);

      const reorderedMiddle = optimizedOrder.map(index => middleWaypoints[index]);

      this.waypoints = [start, ...reorderedMiddle, end];
    },

    initializeDirectionsService() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps not loaded yet');
        return;
      }

      this.directionsService = new google.maps.DirectionsService();
      
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#2563eb',
          strokeWeight: 4,
          strokeOpacity: 0.7
        }
      });

      if (this.$refs.googleMap && this.$refs.googleMap.map) {
        this.directionsRenderer.setMap(this.$refs.googleMap.map);
      }
    },

    async calculateRoute() {
      if (this.waypoints.length < 2) {
        this.errorMessage = 'Please add at least 2 waypoints';
        return;
      }

      if (this.waypoints.length > 23) {
        if (this.optimizeRoute) this.optimizeRoute = false;
        return await this.calculateChunkedRoute();
      }

      if (!this.directionsService || !this.directionsRenderer) {
        this.initializeDirectionsService();
        if (!this.directionsService) {
          this.errorMessage = 'Google Maps Directions Service not initialized';
          return;
        }
      }

      this.isCalculatingRoute = true;
      this.errorMessage = '';

      try {
        const origin = { lat: this.waypoints[0].lat, lng: this.waypoints[0].lng };
        const destination = { 
          lat: this.waypoints[this.waypoints.length - 1].lat, 
          lng: this.waypoints[this.waypoints.length - 1].lng 
        };

        const waypointsForApi = this.waypoints.slice(1, -1).map(wp => ({
          location: { lat: wp.lat, lng: wp.lng },
          stopover: true
        }));

        const request = {
          origin: origin,
          destination: destination,
          waypoints: waypointsForApi,
          optimizeWaypoints: this.optimizeRoute,
          travelMode: google.maps.TravelMode.DRIVING
        };

        this.directionsService.route(request, (result, status) => {
          this.isCalculatingRoute = false;

          if (status === google.maps.DirectionsStatus.OK) {
            this.directionsRenderer.setDirections(result);

            const route = result.routes[0];
            let totalDistance = 0;
            let totalDuration = 0;

            route.legs.forEach(leg => {
              totalDistance += leg.distance.value;
              totalDuration += leg.duration.value;
            });

            this.routeData = {
              distance: (totalDistance / 1000).toFixed(2) + ' km',
              duration: this.formatDuration(totalDuration),
              optimized_order: result.routes[0].waypoint_order
            };

            if (this.optimizeRoute && result.routes[0].waypoint_order && result.routes[0].waypoint_order.length > 0) {
              this.reorderWaypoints(result.routes[0].waypoint_order);
            }

            this.errorMessage = '';
          } else {
            this.errorMessage = `Route calculation failed: ${status}`;
            console.error('Directions request failed:', status);
          }
        });
      } catch (error) {
        this.isCalculatingRoute = false;
        this.errorMessage = 'Error calculating route: ' + error.message;
        console.error('Error calculating route:', error);
      }
    },

    handleMapClick(event) {

      const newWaypoint = {
        id: this.nextWaypointId++,
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      this.waypoints.push(newWaypoint);
      this.errorMessage = '';
      
      if (this.waypoints.length >= 2) {
        setTimeout(() => {
          this.calculateRoute();
        }, 300);
      }
    },

    removeWaypoint(waypointId) {
      if (this.waypoints.length <= 2) {
        this.errorMessage = 'Cannot remove waypoint. Minimum 2 waypoints required.';
        return;
      }

      this.waypoints = this.waypoints.filter(wp => wp.id !== waypointId);
      this.errorMessage = '';
      
      if (this.waypoints.length >= 2) {
        setTimeout(() => {
          this.calculateRoute();
        }, 300);
      }
    },

    selectWaypoint(waypoint) {
      this.selectedWaypointId = waypoint.id;
      
      if (this.$refs.googleMap && this.$refs.googleMap.map) {
        this.$refs.googleMap.map.panTo({ lat: waypoint.lat, lng: waypoint.lng });
      }
    },

    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes} minutes`;
    },

    initPlacesService() {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) return;
        if (this.$refs.googleMap && this.$refs.googleMap.map) {
          this.placesService = new google.maps.places.PlacesService(this.$refs.googleMap.map);
        }
      } catch (e) {
        console.error('Failed to init PlacesService', e);
      }
    },

    onSearchInput() {
      this.showSuggestions = true;
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.predictions = [];
        return;
      }
      if (!window.google || !window.google.maps || !window.google.maps.places) return;

      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        const svc = new google.maps.places.AutocompleteService();
        svc.getPlacePredictions(
          { input: this.searchQuery, types: ['geocode'], componentRestrictions: { country: 'it' } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              this.predictions = predictions;
            } else {
              this.predictions = [];
            }
          }
        );
      }, 250);
    },

    selectPrediction(pred) {
      this.showSuggestions = false;
      this.searchQuery = pred.description;
      if (!this.placesService) this.initPlacesService();
      if (!this.placesService) return;

      this.placesService.getDetails(
        { placeId: pred.place_id, fields: ['geometry.location', 'formatted_address'] },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            this.appendWaypoint(lat, lng);
          }
        }
      );
    },

    appendWaypoint(lat, lng) {
      const newWaypoint = { id: this.nextWaypointId++, lat, lng };
      this.waypoints.push(newWaypoint);
      this.errorMessage = '';
      this.$nextTick(() => this.calculateRoute());
    },
  }
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

